import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { clearOtpState, clearResetState, loginWithPassword } from '../Store/slices/authSlice';
import BrandLogo from '../components/BrandLogo';
import showNotification from '../Utility Components/iFitNotification';

const normalizeRoles = (roles = []) => roles.map((role) => String(role).toLowerCase());

const getWorkspacePath = (roles = []) => {
  const normalized = normalizeRoles(roles);
  if (normalized.some((role) => ['superadmin', 'super_admin', 'admin'].includes(role))) {
    return '/sa-workspace';
  }
  if (normalized.includes('partner')) {
    return '/partners-workspace';
  }
  return null;
};

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, error, access_token, roles } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const prevStatusRef = useRef(status);
  const prevErrorRef = useRef(error);
  const workspacePath = useMemo(() => getWorkspacePath(roles), [roles]);

  useEffect(() => {
    dispatch(clearOtpState());
    dispatch(clearResetState());
  }, [dispatch]);

  useEffect(() => {
    if (access_token && workspacePath) {
      navigate(workspacePath, { replace: true });
    }
  }, [access_token, navigate, workspacePath]);

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      showNotification('error', 'topRight', 'Login failed', String(error));
    }
    prevErrorRef.current = error;
  }, [error]);

  useEffect(() => {
    if (prevStatusRef.current !== 'succeeded' && status === 'succeeded' && access_token) {
      if (workspacePath) {
        showNotification('success', 'topRight', 'Login successful', 'Redirecting to your workspace.');
      } else {
        showNotification('error', 'topRight', 'Workspace not found', 'This account does not have a partner or superadmin role.');
      }
    }
    prevStatusRef.current = status;
  }, [status, access_token, workspacePath]);

  const handleSubmit = (event) => {
    event.preventDefault();
    dispatch(loginWithPassword({ email: formData.identifier, password: formData.password }));
  };

  return (
    <main className="login-screen">
      <section className="login-panel">
        <BrandLogo />
        <p className="eyebrow">Secure workspace access</p>
        <h1>Login to BarbellOS</h1>
        <p className="subtext">
          Use your registered email or mobile number. BarbellOS will open the correct workspace for your account.
        </p>

        <form className="form-grid" onSubmit={handleSubmit}>
          <label>
            Email or Mobile
            <input
              name="identifier"
              type="text"
              required
              value={formData.identifier}
              onChange={(event) => setFormData((prev) => ({ ...prev, identifier: event.target.value }))}
              placeholder="you@barbellos.in or mobile number"
            />
          </label>

          <label>
            Password
            <input
              name="password"
              type="password"
              required
              maxLength={72}
              value={formData.password}
              onChange={(event) => setFormData((prev) => ({ ...prev, password: event.target.value }))}
              placeholder="Enter your password"
            />
          </label>

          <button className="btn-link btn-primary-action" type="submit" disabled={status === 'loading'}>
            <LoginOutlinedIcon fontSize="small" />
            {status === 'loading' ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="login-help">
          <p>Need access or password help?</p>
          <a href="mailto:contact@barbellos.in">contact@barbellos.in</a>
        </div>

        <Link className="btn-link secondary login-home-btn" to="/">
          <HomeOutlinedIcon fontSize="small" />
          Back to homepage
        </Link>
      </section>
    </main>
  );
}

export default LoginPage;
